import React, { useCallback, useState, useEffect, useMemo } from "react";
import Spinner from "../plugins/Spinner";
import useAPI from "../../hooks/useAPI";
import { sendToast } from "../plugins/toastAll";
import { LuBrainCircuit, LuPlus, LuSearch } from "react-icons/lu";
import { Toaster } from "react-hot-toast";
import AssistantCard from "./AssistantCard";
import AssistantEditor from "./AssistantEditor";
import { SCROLL_CLASS, SCROLL_CSS, useFillHeight } from "./BotsContent/Others/BotCard";
import { BOT_LIST_CSS, ConfirmDialog, ListPager, useFullBleed } from "./BotsContent/Others/BotListKit";
import usePage from "../../hooks/usePage";

const STATUS_CHIPS = [
  { label: "Activo", value: true, color: "#2fae4e" },
  { label: "Inactivo", value: false, color: "#8B8B8B" },
];

const AssistantsList = ({ assistants, loadingDone, onNew, onEdit, onDelete, onToggleActive }) => {
  const { page, setPage, byPage, setByPage } = usePage();
  const [listRootRef, listRootHeight] = useFillHeight(0);
  useFullBleed(listRootRef);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(null);

  useEffect(() => {
    setByPage(12);
  }, [setByPage]);

  useEffect(() => {
    setPage(1);
  }, [search, status, setPage]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return [...assistants]
      .reverse()
      .filter((assistant) => !query || String(assistant.name || "").toLowerCase().includes(query))
      .filter((assistant) => status === null || (assistant.active !== false) === status);
  }, [assistants, search, status]);

  const empty = loadingDone && assistants.length === 0;

  return (
    <>
      <style>{SCROLL_CSS}</style>
      <style>{BOT_LIST_CSS}</style>
      <div ref={listRootRef} className="su-kit su-list-root d-flex flex-column" style={{ height: listRootHeight ? `${listRootHeight}px` : undefined }}>
        <div className="su-head">
          <div className="col-xs-9 heading">
            <div className="d-flex align-items-center" style={{ gap: "14px", minWidth: 0 }}>
              <h1>Asistentes</h1>
              {!empty && (
                <span className="su-count">
                  {filtered.length === assistants.length ? `${assistants.length} asistente${assistants.length === 1 ? "" : "s"}` : `${filtered.length} de ${assistants.length} asistentes`}
                </span>
              )}
            </div>
            {!empty && (
              <div className="su-head-right">
                <div className="su-search">
                  <LuSearch className="su-ic" size={17} />
                  <input
                    type="text"
                    id="assistantSearch"
                    placeholder="Buscar asistente por nombre"
                    aria-label="Buscar asistente por nombre"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </div>
                <button type="button" className="su-btn" onClick={onNew}>
                  <LuPlus className="su-ic" size={16} />
                  Nuevo asistente
                </button>
              </div>
            )}
          </div>
          {!empty && (
            <div className="su-chips su-filters">
              {STATUS_CHIPS.map((chip) => (
                <button
                  key={chip.label}
                  type="button"
                  className={`su-chip${status === chip.value ? " on" : ""}`}
                  style={{ "--c": chip.color }}
                  aria-pressed={status === chip.value}
                  onClick={() => setStatus(status === chip.value ? null : chip.value)}
                >
                  <i />
                  {chip.label}
                </button>
              ))}
              {status !== null && (
                <button type="button" className="su-link" onClick={() => setStatus(null)}>
                  Limpiar filtros
                </button>
              )}
            </div>
          )}
        </div>

        <div className={`su-board ${SCROLL_CLASS}`}>
          {empty ? (
            <div className="su-empty">
              <LuBrainCircuit className="su-ic" size={44} />
              <b>Todavía no tienes asistentes</b>
              <span>Crea un asistente con la información de tu empresa y úsalo en tus bots.</span>
              <div className="su-empty-acts">
                <button type="button" className="su-btn" onClick={onNew}>
                  <LuPlus className="su-ic" size={16} />
                  Crear mi primer asistente
                </button>
              </div>
            </div>
          ) : assistants.length > 0 && filtered.length === 0 ? (
            <div className="su-empty">
              <LuSearch className="su-ic" size={40} />
              <b>{search.trim() ? `No hay asistentes con «${search.trim()}»` : "Ningún asistente coincide con este filtro"}</b>
              <span>{search.trim() ? "Revisa cómo está escrito o busca otro nombre." : "Quita el filtro para ver más asistentes."}</span>
              <div className="su-empty-acts">
                <button
                  type="button"
                  className="su-btn ghost"
                  onClick={() => {
                    setSearch("");
                    setStatus(null);
                  }}
                >
                  {search.trim() ? "Limpiar búsqueda" : "Limpiar filtros"}
                </button>
                <button type="button" className="su-btn" onClick={onNew}>
                  Crear asistente
                </button>
              </div>
            </div>
          ) : (
            <div className="su-grid">
              {filtered.slice((page - 1) * byPage, (page - 1) * byPage + byPage).map((assistant) => (
                <AssistantCard key={assistant.id} assistant={assistant} onEdit={onEdit} onDelete={onDelete} onToggleActive={onToggleActive} />
              ))}
            </div>
          )}
        </div>

        <ListPager total={filtered.length} page={page} byPage={byPage} setPage={setPage} />
      </div>
    </>
  );
};

const Assistants = () => {
  const [loading, setLoading] = useState(false);
  const [loadingDone, setLoadingDone] = useState(false);
  const [listAssistants, setListAssistants] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [listAreas, setListAreas] = useState([]);
  const [listAdvisers, setListAdvisers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const closeConfirm = useCallback(() => setConfirm(null), []);
  const [crmOptions, setCrmOptions] = useState({ stages: [], lists: [], tags: [], fields: [] });

  const { urlAPI_1, urlAPI_2, urlAPI_3, fetchWithAuth } = useAPI();

  const getCrmOptions = async () => {
    const load = async (url, pick) => {
      try {
        const response = await fetchWithAuth(url, { method: "GET", mode: "cors" });
        const result = await response.json();
        return pick(result) || [];
      } catch (error) {
        return [];
      }
    };
    const [stages, lists, tags, fields] = await Promise.all([
      load(urlAPI_3 + "stages", (result) => (Array.isArray(result) ? result : result?.data)),
      load(urlAPI_1 + "lists/options", (result) => result?.data),
      load(urlAPI_1 + "tags", (result) => result?.data),
      load(urlAPI_2 + "aditionalFields", (result) => (result?.type === "success" ? result.data : [])),
    ]);
    setCrmOptions({
      stages: stages.filter((stage) => stage?._id && stage?.name).map((stage) => ({ _id: stage._id, name: stage.name })),
      lists: lists.filter((list) => list?._id && list?.name).map((list) => ({ _id: list._id, name: list.name })),
      tags: tags.filter((tag) => tag?._id && tag?.title).map((tag) => ({ _id: tag._id, title: tag.title })),
      fields: fields.filter((field) => field?.name).map((field) => ({ name: field.name })),
    });
  };

  const getAssistants = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(urlAPI_3 + "assistants", { method: "GET", mode: "cors" });
      const result = await response.json();
      if (result.type === "error") {
        sendToast(result);
      } else {
        setListAssistants(result.data || []);
      }
    } catch (error) {
      console.log("error al listar asistentes", error);
    } finally {
      setLoading(false);
      setLoadingDone(true);
    }
  };

  const getTemplates = async () => {
    try {
      const response = await fetchWithAuth(urlAPI_2 + "templates", { method: "GET", mode: "cors" });
      const result = await response.json();
      setTemplates(result.type === "success" ? (result.data || []).filter((template) => template.status === "APROBADA" && template.isActive === true) : []);
    } catch (error) {
      console.log("error al consultar las plantillas", error);
    }
  };

  const getAreas = async () => {
    try {
      const response = await fetchWithAuth(urlAPI_2 + "getAreasByCompany", { method: "GET", mode: "cors" });
      const result = await response.json();
      setListAreas(result.code === 0 ? [] : (result.data || []).map((area) => ({ label: area.description, value: area._id, type: "area" })));
    } catch (error) {
      console.log("error al consultar las áreas", error);
    }
  };

  const getAgents = async () => {
    try {
      const response = await fetchWithAuth(urlAPI_2 + "usersbyArea", { method: "GET", mode: "cors" });
      const result = await response.json();
      setListAdvisers(result.code === 0 ? [] : (result.data || []).map((user) => ({ label: user.name, value: user._id, type: "agent" })));
    } catch (error) {
      console.log("error al consultar los asesores", error);
    }
  };

  useEffect(() => {
    getAssistants();
    getAreas();
    getAgents();
    getTemplates();
    getCrmOptions();
  }, []);

  const deleteAssistant = async (id) => {
    const assistant = listAssistants.find((item) => item.id === id);
    const botsUsing = assistant?.usedIn || [];
    if (botsUsing.length > 0) {
      const names = botsUsing.map((bot) => bot.name).join(", ");
      sendToast({
        type: "warning",
        message: `El asistente *${assistant?.name || ""}+ está siendo usado por ${botsUsing.length === 1 ? "el bot" : "los bots"} *${names}+. No se puede eliminar.`,
        strong: true,
      });
      return;
    }
    setConfirm({
      title: `Eliminar «${assistant?.name || ""}»`,
      text: "No podrás recuperarlo.",
      action: "Eliminar asistente",
      run: async () => {
        try {
          setLoading(true);
          const response = await fetchWithAuth(urlAPI_3 + "assistant/" + id, { method: "DELETE", mode: "cors" });
          const result = await response.json();
          sendToast(result?.message ? result : { type: "error", message: "No se pudo eliminar el asistente." });
          await getAssistants();
        } catch (error) {
          sendToast({ type: "error", message: "No se pudo eliminar el asistente." });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const changeActive = async (id, active) => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(`${urlAPI_3}assistant/${id}/status`, { method: "POST", mode: "cors", body: JSON.stringify({ active }) });
      const result = await response.json();
      sendToast(result?.message ? result : { type: "error", message: "No se pudo cambiar el estado." });
      if (response.ok && result.type === "success") {
        setListAssistants((prev) => prev.map((item) => (item.id === id ? { ...item, active } : item)));
      }
    } catch (error) {
      sendToast({ type: "error", message: "No se pudo cambiar el estado." });
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = (id, active) => {
    const assistant = listAssistants.find((item) => item.id === id);
    if (!active && assistant?.usedIn?.length) {
      setConfirm({
        title: `Desactivar «${assistant.name}»`,
        text: `Lo usa ${assistant.usedIn.map((bot) => bot.name).join(", ")}. Mientras esté inactivo no responderá en esos bots.`,
        action: "Desactivar",
        run: () => changeActive(id, active),
      });
      return;
    }
    changeActive(id, active);
  };

  return (
    <>
      {loading ? (
        <div className="content-spinner">
          <Spinner />
        </div>
      ) : null}

      {editing ? (
        <AssistantEditor
          key={editing.id || "new"}
          assistant={editing.id ? editing : null}
          listAreas={listAreas}
          listAdvisers={listAdvisers}
          templates={templates}
          crmOptions={crmOptions}
          onBack={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await getAssistants();
          }}
        />
      ) : (
        <AssistantsList
          assistants={listAssistants}
          loadingDone={loadingDone}
          onNew={() => setEditing({})}
          onEdit={(id) => setEditing(listAssistants.find((assistant) => assistant.id === id) || null)}
          onDelete={deleteAssistant}
          onToggleActive={toggleActive}
        />
      )}

      <ConfirmDialog confirm={confirm} onClose={closeConfirm} />

      <Toaster containerClassName="su-toasts" />
    </>
  );
};

export default Assistants;
